import { deleteCookie, setCookie } from 'cookies-next';
import dayjs from 'dayjs';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import appConstants from '@/constants/appConstants';
import type { User } from '@/features/users/types/User';
import { encrypt } from '@/helpers';

import type { TempForgotPasswordData } from '../types/TempForgotPasswordData';
import type { Token } from '../types/Token';

export type AuthState = {
    user: User | null;
    token: Token | null;
    tempForgotPasswordData: TempForgotPasswordData | null;
};

export type AuthActions = {
    updateUser: (user: AuthState['user'], options?: { expires: Date }) => Promise<void>;
    updateToken: (token: AuthState['token'], options?: { expires: Date }) => Promise<void>;
    updateTempForgotPasswordData: (tempForgotPasswordData: AuthState['tempForgotPasswordData'], options?: { expires: Date }) => void;
    resetStore: () => void;
};

export type AuthStore = AuthState & AuthActions;

export const initialState: AuthState = {
    user: null,
    token: null,
    tempForgotPasswordData: null,
};

export function createAuthStore(authState: AuthState = initialState): StoreApi<AuthStore> {
    return createStore<AuthStore>()((set) => ({
        ...authState,
        updateUser: async (user, { expires } = { expires: dayjs().toDate() }): Promise<void> => {
            const encryptedUserDetails = await encrypt(JSON.stringify(user));
            setCookie(appConstants.authCookieName, encryptedUserDetails, {
                sameSite: process.env.NODE_ENV !== 'development',
                secure: process.env.NODE_ENV !== 'development',
                expires,
            });
            set(() => ({ user }));
        },
        updateToken: async (token, { expires } = { expires: dayjs().toDate() }): Promise<void> => {
            const encryptedToken = await encrypt(JSON.stringify(token));
            setCookie(appConstants.tokenCookieName, encryptedToken, {
                sameSite: process.env.NODE_ENV !== 'development',
                secure: process.env.NODE_ENV !== 'development',
                expires,
            });
            set(() => ({ token }));
        },
        updateTempForgotPasswordData: (tempForgotPasswordData, { expires } = { expires: dayjs().add(10, 'minutes').toDate() }): void => {
            setCookie(appConstants.forgotPasswordCookieName, tempForgotPasswordData, {
                sameSite: process.env.NODE_ENV !== 'development',
                secure: process.env.NODE_ENV !== 'development',
                expires,
            });
            set(() => ({ tempForgotPasswordData }));
        },
        resetStore: (): void => {
            deleteCookie(appConstants.authCookieName);
            deleteCookie(appConstants.tokenCookieName);
            deleteCookie(appConstants.forgotPasswordCookieName);
            set(initialState);
        },
    }));
}
